import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Spade, ArrowDownLeft, ArrowUpRight, Coins, Percent, RefreshCw, Flag } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * ThreeCardPokerReportCard — admin financial snapshot for the in-house
 * 3-Card Poker table. Simple all-time aggregation from db.tcp_games.
 */
export default function ThreeCardPokerReportCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/tcp/report`, { withCredentials: true });
            setData(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load 3-Card Poker report');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (!data) {
        return (
            <div className="card-dark p-5 text-[#A0AAB2] text-xs" data-testid="tcp-report-loading">
                Loading 3-Card Poker report…
            </div>
        );
    }

    return (
        <div className="card-dark p-5 space-y-4" data-testid="tcp-report-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Spade className="w-5 h-5 text-[#00E1FF]" />
                    <h3 className="text-sm uppercase font-bold tracking-wide text-white">3-Card Poker — Ins &amp; Outs</h3>
                    <span className="text-[10px] text-[#A0AAB2]">house table · all-time</span>
                </div>
                <button onClick={load} disabled={loading} className="text-xs text-[#A0AAB2] hover:text-white flex items-center gap-1" data-testid="tcp-report-refresh">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <Stat label="Rounds" value={data.rounds} testId="tcp-report-rounds" />
                <Stat label="Wagered" value={fmt(data.wagered)} icon={<ArrowDownLeft className="w-3 h-3 text-[#39FF14]" />} valueClass="text-[#39FF14]" testId="tcp-report-wagered" />
                <Stat label="Paid out" value={fmt(data.paid_out)} icon={<ArrowUpRight className="w-3 h-3 text-[#FF3B30]" />} valueClass="text-[#FF3B30]" testId="tcp-report-paid" />
                <Stat label="House net" value={`${data.profit >= 0 ? '+' : ''}${fmt(data.profit)}`} icon={<Coins className="w-3 h-3 text-[#FFD700]" />} valueClass={data.profit >= 0 ? 'text-[#FFD700]' : 'text-[#FF3B30]'} testId="tcp-report-profit" />
                <Stat label="RTP %" value={`${(data.rtp_pct || 0).toFixed(2)}%`} icon={<Percent className="w-3 h-3 text-[#00E1FF]" />} valueClass="text-white" testId="tcp-report-rtp" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Stat label="Ante $" value={fmt(data.ante_wagered)} testId="tcp-report-ante" />
                <Stat label="Play $" value={fmt(data.play_wagered)} testId="tcp-report-play" />
                <Stat label="Pair Plus $" value={fmt(data.pair_plus_wagered)} testId="tcp-report-pp" />
                <Stat label="Fold rate" value={`${(data.fold_rate_pct || 0).toFixed(1)}%`} icon={<Flag className="w-3 h-3 text-[#FF3B30]" />} testId="tcp-report-folds" />
            </div>
        </div>
    );
}

const Stat = ({ label, value, icon, valueClass = 'text-white', testId }) => (
    <div className="rounded-lg border border-white/10 bg-[#0e0e0e] p-3" data-testid={testId}>
        <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold flex items-center gap-1">{icon}{label}</p>
        <p className={`mt-1 text-lg font-black font-mono ${valueClass}`}>{value}</p>
    </div>
);
